import { useEffect, useState } from "react";
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography,
} from "@mui/material";
import {
  DEFAULT_PRESET_PV_KWP, DEFAULT_TILT, FACINGS, PRESET_PRICES, PRESET_SHADING_PCT, STUDY_PRESET_MODULE,
  facingLabel, studyKindOptions,
} from "./config/studyPresets";
import { getBatteries, getInverters, getModules } from "../services/api";

// An optional positive figure: blank is allowed (filled in later), anything
// else must be a positive number. A decimal comma reads as a point.
const optionalPositive = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return { value: null, invalid: false };
  const n = Number(text.replace(",", "."));
  return Number.isFinite(n) && n > 0 ? { value: n, invalid: false } : { value: null, invalid: true };
};
const itemName = (b) => String(b?.battery_name ?? b?.name ?? "").trim();
const asList = (data) => (Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : []);

/**
 * «Γρήγορη μελέτη» — opened by File → New. The user picks the kind of study
 * («Τι μελέτη θέλετε;») and the project starts from its settings
 * (config/studyPresets.js), with whatever customer figures and equipment are
 * given here. The project is saved when it is created.
 *
 * - «Δημιουργία έργου» creates and saves it, and the user goes on by hand.
 * - «Δημιουργία και εκτέλεση» creates it and runs the rest by itself once the
 *   location is set: auto-design, design save, study, results card. It needs
 *   the consumption and the price (and the battery, for PV with battery).
 * - «Παράλειψη» opens a new project with the platform's basic settings
 *   (DEFAULT_CONFIG), not saved, as File → New did before.
 * - Cancel and Esc leave the open project open and untouched.
 *
 * `onCreate(choice, { run })` may reject: the dialog then stays open and shows
 * why (a name already in use, the plan's project limit).
 */
export default function NewStudyDialog({ open, isGreek, openProjectName, onCancel, onCreate, onSkip }) {
  const tr = (el, en) => (isGreek ? el : en);
  const [kind, setKind] = useState("net_billing");
  const [useCase, setUseCase] = useState("Commercial");
  const [pvKwp, setPvKwp] = useState(String(DEFAULT_PRESET_PV_KWP));
  const [name, setName] = useState("");
  const [annualKwh, setAnnualKwh] = useState("");
  const [price, setPrice] = useState("");
  const [mounting, setMounting] = useState("roof_frame");
  const [tilt, setTilt] = useState(String(DEFAULT_TILT));
  const [azimuth, setAzimuth] = useState(180);
  const [module, setModule] = useState(STUDY_PRESET_MODULE);
  const [inverter, setInverter] = useState(null);
  const [battery, setBattery] = useState(null);
  const [catalog, setCatalog] = useState({ modules: null, inverters: null, batteries: null, error: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!open) return;
    setKind("net_billing");
    setUseCase("Commercial");
    setPvKwp(String(DEFAULT_PRESET_PV_KWP));
    setName(""); setAnnualKwh(""); setPrice("");
    setMounting("roof_frame"); setTilt(String(DEFAULT_TILT)); setAzimuth(180);
    setModule(STUDY_PRESET_MODULE); setInverter(null); setBattery(null);
    setBusy(false); setError("");
  }, [open]);
  // The module and inverter catalogs, loaded once, when the dialog opens.
  useEffect(() => {
    if (!open || catalog.modules) return undefined;
    let alive = true;
    Promise.all([getModules(""), getInverters("")])
      .then(([modules, inverters]) => {
        if (alive) setCatalog((c) => ({ ...c, modules: asList(modules), inverters: asList(inverters) }));
      })
      .catch(() => {
        if (alive) setCatalog((c) => ({ ...c, modules: [], inverters: [], error: tr(
          "Δεν φορτώθηκε ο κατάλογος εξοπλισμού· χρησιμοποιούνται οι προεπιλογές. Αλλάζουν αργότερα, στη Ρύθμιση Μονάδας → ΦΒ Σύστημα.",
          "The equipment catalog could not be loaded; the defaults are used. They can be changed later, under Unit Setup → PV System.") }));
      });
    return () => { alive = false; };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  // The whole battery catalog, loaded the first time it is needed.
  useEffect(() => {
    if (!open || kind !== "pv_battery" || catalog.batteries) return undefined;
    let alive = true;
    getBatteries({})
      .then((list) => { if (alive) setCatalog((c) => ({ ...c, batteries: asList(list) })); })
      .catch(() => { if (alive) setCatalog((c) => ({ ...c, batteries: [], batteryError: true })); });
    return () => { alive = false; };
  }, [open, kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const options = studyKindOptions(isGreek);
  const kwp = Number(String(pvKwp).replace(",", "."));
  const kwpInvalid = !(Number.isFinite(kwp) && kwp > 0);
  const tiltNum = Number(String(tilt).replace(",", "."));
  const tiltInvalid = String(tilt).trim() === "" || !(Number.isFinite(tiltNum) && tiltNum >= 0 && tiltNum < 90);
  const kwh = optionalPositive(annualKwh);
  const eurKwh = optionalPositive(price);
  const invalid = kwpInvalid || tiltInvalid || kwh.invalid || eurKwh.invalid;
  const figuresGiven = kwh.value != null && eurKwh.value != null;
  const batteryMissing = kind === "pv_battery" && !battery;
  const runMissing = [
    ...(kwh.value == null ? [tr("ετήσια κατανάλωση", "annual consumption")] : []),
    ...(eurKwh.value == null ? [tr("τιμή ρεύματος", "electricity price")] : []),
    ...(batteryMissing ? [tr("μπαταρία", "battery")] : []),
  ];
  const steps = [
    tr("θέση στον χάρτη", "location on the map"),
    ...(figuresGiven ? [] : [tr("κατανάλωση και τιμή ρεύματος", "consumption and electricity price")]),
    ...(batteryMissing ? [tr("μπαταρία", "battery")] : []),
    tr("αυτόματος σχεδιασμός", "auto-design"),
    tr("αποθήκευση σχεδίου", "save the design"),
    tr("εκτέλεση", "run"),
  ];
  const flush = mounting === "roof_flush";
  const catalogLoading = open && !catalog.modules;

  const create = async (run) => {
    setBusy(true); setError("");
    try {
      await onCreate({
        kind, useCase, pvKwp: kwp, name: name.trim(),
        annualKwh: kwh.value, priceEurKwh: eurKwh.value,
        battery: kind === "pv_battery" ? battery : null,
        module: module || STUDY_PRESET_MODULE, inverter: inverter ? itemName(inverter) : "",
        mounting, tilt: tiltNum, azimuth,
      }, { run });
    } catch (err) {
      setError(String(err?.message || err || tr("Το έργο δεν δημιουργήθηκε.", "The project was not created.")));
    } finally {
      setBusy(false);
    }
  };

  const section = (title) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>{title}</Typography>
  );

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="sm" fullWidth aria-labelledby="new-study-title">
      <DialogTitle id="new-study-title">{tr("Γρήγορη μελέτη", "Quick study")}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }} id="new-study-kind-label">
          {tr("Τι μελέτη θέλετε;", "What study do you want?")}
        </Typography>
        <Stack spacing={1.25} role="radiogroup" aria-labelledby="new-study-kind-label">
          {options.map((option) => {
            const selected = option.value === kind;
            return (
              <Box key={option.value} role="radio" aria-checked={selected} tabIndex={0}
                data-testid={`new-study-${option.value}`}
                onClick={() => setKind(option.value)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setKind(option.value); } }}
                sx={{
                  border: 2, borderColor: selected ? "primary.main" : "divider", borderRadius: 2,
                  p: 1.5, cursor: "pointer", bgcolor: selected ? "action.selected" : "transparent",
                  "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
                }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{option.title}</Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>{option.text}</Typography>
              </Box>
            );
          })}
        </Stack>

        <Box data-testid="new-study-fields">
          {section(tr("Στοιχεία πελάτη", "Customer details"))}
          <Stack spacing={1.5}>
            {/* Two fields per row, so the labels with their units are never cut off. */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField size="small" sx={{ flex: 2 }} label={tr("Όνομα πελάτη / έργου", "Customer / project name")}
                value={name} onChange={(e) => setName(e.target.value)}
                inputProps={{ "data-testid": "new-study-name", maxLength: 200 }}
                helperText={tr("Το έργο αποθηκεύεται με αυτό το όνομα· κενό = «Χωρίς τίτλο».", "The project is saved under this name; blank = \"Untitled\".")} />
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel id="new-study-consumer-label">{tr("Τύπος καταναλωτή", "Consumer type")}</InputLabel>
                <Select labelId="new-study-consumer-label" label={tr("Τύπος καταναλωτή", "Consumer type")}
                  value={useCase} onChange={(e) => setUseCase(e.target.value)}
                  inputProps={{ "data-testid": "new-study-consumer" }}>
                  <MenuItem value="Commercial">{tr("Επιχείρηση", "Business")}</MenuItem>
                  <MenuItem value="Residential Urban">{tr("Κατοικία", "Household")}</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField size="small" sx={{ flex: 1 }} label={tr("Ετήσια κατανάλωση (kWh)", "Annual consumption (kWh)")}
                value={annualKwh} onChange={(e) => setAnnualKwh(e.target.value)} error={kwh.invalid}
                inputProps={{ inputMode: "decimal", "data-testid": "new-study-kwh" }}
                helperText={kwh.invalid ? tr("Δώστε θετικό αριθμό ή αφήστε κενό.", "Enter a positive number or leave blank.") : " "} />
              <TextField size="small" sx={{ flex: 1 }} label={tr("Τιμή ρεύματος (€/kWh)", "Electricity price (€/kWh)")}
                value={price} onChange={(e) => setPrice(e.target.value)} error={eurKwh.invalid}
                inputProps={{ inputMode: "decimal", "data-testid": "new-study-price" }}
                helperText={eurKwh.invalid
                  ? tr("Δώστε θετικό αριθμό ή αφήστε κενό.", "Enter a positive number or leave blank.")
                  : tr("Τελική τιμή, με χρεώσεις δικτύου και ΥΚΩ.", "Final price, with network charges and PSO.")} />
            </Stack>
          </Stack>

          {section(tr("Σύστημα", "System"))}
          <Stack spacing={1.5}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField size="small" sx={{ flex: 1 }} label={tr("Ισχύς Φ/Β (kWp)", "PV size (kWp)")}
                value={pvKwp} onChange={(e) => setPvKwp(e.target.value)} error={kwpInvalid}
                inputProps={{ inputMode: "decimal", "data-testid": "new-study-kwp" }}
                helperText={kwpInvalid
                  ? tr("Δώστε θετικό αριθμό.", "Enter a positive number.")
                  : tr("Ο στόχος του αυτόματου σχεδιασμού.", "The auto-design target.")} />
              <FormControl size="small" sx={{ flex: 1.4 }}>
                <InputLabel id="new-study-mounting-label">{tr("Τοποθέτηση", "Mounting")}</InputLabel>
                <Select labelId="new-study-mounting-label" label={tr("Τοποθέτηση", "Mounting")}
                  value={mounting} onChange={(e) => setMounting(e.target.value)}
                  inputProps={{ "data-testid": "new-study-mounting" }}>
                  <MenuItem value="roof_frame">{tr("Επίπεδη στέγη / δώμα, σε βάσεις", "Flat roof, on frames")}</MenuItem>
                  <MenuItem value="roof_flush">{tr("Κεκλιμένη στέγη, πάνω στα κεραμίδια", "Pitched roof, on the tiles")}</MenuItem>
                  <MenuItem value="ground">{tr("Έδαφος", "Ground")}</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField size="small" sx={{ flex: 1 }} label={flush ? tr("Κλίση στέγης (°)", "Roof pitch (°)") : tr("Κλίση πάνελ (°)", "Panel tilt (°)")}
                value={tilt} onChange={(e) => setTilt(e.target.value)} error={tiltInvalid}
                inputProps={{ inputMode: "decimal", "data-testid": "new-study-tilt" }}
                helperText={tiltInvalid ? tr("Από 0 έως κάτω από 90.", "From 0 to below 90.") : " "} />
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel id="new-study-facing-label">{flush ? tr("Προσανατολισμός στέγης", "Roof facing") : tr("Προσανατολισμός", "Facing")}</InputLabel>
                <Select labelId="new-study-facing-label" label={flush ? tr("Προσανατολισμός στέγης", "Roof facing") : tr("Προσανατολισμός", "Facing")}
                  value={azimuth} onChange={(e) => setAzimuth(Number(e.target.value))}
                  inputProps={{ "data-testid": "new-study-facing" }}>
                  {FACINGS.map((a) => <MenuItem key={a} value={a}>{facingLabel(a, isGreek)}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Autocomplete size="small" sx={{ flex: 1 }} disableClearable
                options={[...new Set([STUDY_PRESET_MODULE, ...(catalog.modules || []).map(itemName).filter(Boolean)])]}
                loading={catalogLoading} value={module}
                onChange={(_, value) => setModule(value || STUDY_PRESET_MODULE)}
                renderOption={(props, option) => <li {...props} key={option}><span data-no-translate>{option}</span></li>}
                renderInput={(params) => (
                  <TextField {...params} label={tr("Πάνελ", "Module")}
                    inputProps={{ ...params.inputProps, "data-testid": "new-study-module" }} />
                )} />
              <Autocomplete size="small" sx={{ flex: 1 }}
                options={catalog.inverters || []} loading={catalogLoading}
                value={inverter} onChange={(_, value) => setInverter(value)}
                getOptionLabel={itemName} isOptionEqualToValue={(a, b) => itemName(a) === itemName(b)}
                renderOption={(props, item) => <li {...props} key={itemName(item)}><span data-no-translate>{itemName(item)}</span></li>}
                renderInput={(params) => (
                  <TextField {...params} label={tr("Αντιστροφέας", "Inverter")}
                    placeholder={tr("Αυτόματα", "Automatic")}
                    inputProps={{ ...params.inputProps, "data-testid": "new-study-inverter" }}
                    helperText={tr("Κενό: τον επιλέγει ο αυτόματος σχεδιασμός.", "Blank: auto-design chooses it.")} />
                )} />
            </Stack>
            {kind === "pv_battery" ? (
              <Autocomplete size="small" fullWidth
                options={catalog.batteries || []} loading={open && !catalog.batteries}
                value={battery} onChange={(_, value) => setBattery(value)}
                getOptionLabel={itemName} isOptionEqualToValue={(a, b) => itemName(a) === itemName(b)}
                renderOption={(props, b) => <li {...props} key={itemName(b)}><span data-no-translate>{itemName(b)}</span></li>}
                renderInput={(params) => (
                  <TextField {...params} label={tr("Μπαταρία", "Battery")}
                    inputProps={{ ...params.inputProps, "data-testid": "new-study-battery" }}
                    helperText={catalog.batteryError
                      ? tr("Δεν φορτώθηκε ο κατάλογος μπαταριών. Μπορείτε να επιλέξετε μπαταρία αργότερα, στη Ρύθμιση Μονάδας → Αποθήκευση.", "The battery catalog could not be loaded. You can choose a battery later, under Unit Setup → Storage.")
                      : tr("Από τον κατάλογο.", "From the catalog.")} />
                )} />
            ) : null}
            {catalog.error ? <Alert severity="info">{catalog.error}</Alert> : null}
          </Stack>
        </Box>

        <Typography variant="caption" component="p" sx={{ color: "text.secondary", mt: 1.5 }} data-testid="new-study-defaults">
          {tr(`Αρχικές ρυθμίσεις: Χαμηλή Τάση 400 V, σκίαση ${PRESET_SHADING_PCT}% ως ετήσιο ποσοστό, ετήσια κατανάλωση, μία συνολική τιμή ανά kWh, ενδεικτικό κόστος Φ/Β ${PRESET_PRICES.pvEurPerKwp} €/kWp χωρίς ΦΠΑ${kind === "pv_battery" ? ` και μπαταρίας ${PRESET_PRICES.batteryEurPerKwh} €/kWh` : ""}, 20 έτη, προεξόφληση 6%, χωρίς δάνειο. Όλα αλλάζουν στη συνέχεια.`,
            `Starting settings: low voltage 400 V, shading ${PRESET_SHADING_PCT}% as an annual percentage, annual consumption, one total price per kWh, indicative PV cost ${PRESET_PRICES.pvEurPerKwp} €/kWp before VAT${kind === "pv_battery" ? ` and battery ${PRESET_PRICES.batteryEurPerKwh} €/kWh` : ""}, 20 years, 6% discount rate, no loan. Everything can be changed afterwards.`)}
          <br />
          {tr(`Το έργο αποθηκεύεται με τη δημιουργία. Επόμενα βήματα: ${steps.join(" → ")}.`,
            `The project is saved when it is created. Next steps: ${steps.join(" → ")}.`)}
          <br />
          {runMissing.length
            ? tr(`Για «Δημιουργία και εκτέλεση» χρειάζονται: ${runMissing.join(", ")}.`,
              `"Create and run" needs: ${runMissing.join(", ")}.`)
            : tr("Με «Δημιουργία και εκτέλεση» ορίζετε μόνο τη θέση στον χάρτη· τα υπόλοιπα γίνονται αυτόματα.",
              "With \"Create and run\" you only set the location on the map; the rest is done for you.")}
        </Typography>
        {error ? <Alert severity="error" sx={{ mt: 1.5 }} data-testid="new-study-error">{error}</Alert> : null}
      </DialogContent>
      {openProjectName ? (
        <Typography variant="caption" component="p" data-testid="new-study-open-project"
          sx={{ px: 3, pt: 1.5, color: "text.secondary" }}>
          {tr(`Το ανοιχτό έργο «${openProjectName}» θα κλείσει. Με «Άκυρο» παραμένει ανοιχτό.`,
            `The open project "${openProjectName}" will close. Cancel keeps it open.`)}
        </Typography>
      ) : null}
      <DialogActions sx={{ px: 3, py: 1.5, flexWrap: "wrap", gap: 1 }}>
        <Button onClick={onSkip} disabled={busy} data-testid="new-study-skip" sx={{ mr: "auto" }}>
          {tr("Παράλειψη (βασικές ρυθμίσεις)", "Skip (basic settings)")}
        </Button>
        <Button onClick={onCancel} disabled={busy}>{tr("Άκυρο", "Cancel")}</Button>
        <Button variant="outlined" disabled={invalid || busy} data-testid="new-study-create" onClick={() => create(false)}>
          {tr("Δημιουργία έργου", "Create project")}
        </Button>
        <Button variant="contained" disabled={invalid || busy || runMissing.length > 0} data-testid="new-study-create-run"
          onClick={() => create(true)}
          startIcon={busy ? <CircularProgress color="inherit" size={16} /> : undefined}>
          {tr("Δημιουργία και εκτέλεση", "Create and run")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
